/** Hash worker client + IndexedDB manifest cache.
 *
 * Cache key: (sessionKey, path, mtime, size). On hit, we reuse the stored
 * hash and skip reading file bytes entirely — the big win for repeated
 * reverseSync calls on unchanged workspaces.
 */

const DB_NAME = "strands-docsync-cache";
const STORE = "manifest";
const DB_VERSION = 1;

export interface CacheEntry {
  path: string;
  mtime: number;
  size: number;
  hash: string;
}

interface StoredEntry extends CacheEntry {
  key: string;
  sessionKey: string;
}

const compoundKey = (sessionKey: string, path: string) => `${sessionKey}|${path}`;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
      const store = db.createObjectStore(STORE, { keyPath: "key" });
      store.createIndex("sessionKey", "sessionKey", { unique: false });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function txPromise<T>(tx: IDBTransaction, result: () => T): Promise<T> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(result());
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function loadCache(sessionKey: string, paths: string[]): Promise<Map<string, CacheEntry>> {
  if (paths.length === 0) return new Map();
  const db = await openDB();
  const tx = db.transaction(STORE, "readonly");
  const store = tx.objectStore(STORE);
  const out = new Map<string, CacheEntry>();
  for (const p of paths) {
    const req = store.get(compoundKey(sessionKey, p));
    req.onsuccess = () => {
      const r = req.result as StoredEntry | undefined;
      if (r) out.set(p, { path: r.path, mtime: r.mtime, size: r.size, hash: r.hash });
    };
  }
  return txPromise(tx, () => out);
}

export async function replaceCache(sessionKey: string, entries: CacheEntry[]): Promise<void> {
  const db = await openDB();
  const tx = db.transaction(STORE, "readwrite");
  const store = tx.objectStore(STORE);
  const keep = new Set(entries.map(e => compoundKey(sessionKey, e.path)));

  const idx = store.index("sessionKey");
  const cursorReq = idx.openKeyCursor(IDBKeyRange.only(sessionKey));
  cursorReq.onsuccess = () => {
    const cur = cursorReq.result;
    if (!cur) return;
    if (!keep.has(cur.primaryKey as string)) store.delete(cur.primaryKey);
    cur.continue();
  };

  for (const e of entries) {
    const stored: StoredEntry = { ...e, key: compoundKey(sessionKey, e.path), sessionKey };
    store.put(stored);
  }
  return txPromise(tx, () => undefined);
}

let worker: Worker | null = null;
let seq = 0;
const pending = new Map<number, { resolve: (hex: string) => void; reject: (e: unknown) => void }>();

function getWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL("./hashWorker.ts", import.meta.url), { type: "module" });
  worker.onmessage = (e: MessageEvent<{ id: number; hex?: string; error?: string }>) => {
    const p = pending.get(e.data.id);
    if (!p) return;
    pending.delete(e.data.id);
    if (e.data.error) p.reject(new Error(e.data.error));
    else p.resolve(e.data.hex!);
  };
  return worker;
}

export function hashBuffer(buf: ArrayBuffer): Promise<string> {
  const w = getWorker();
  const id = ++seq;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage({ id, data: buf }, [buf]);
  });
}

export async function hashString(s: string): Promise<string> {
  const buf = new TextEncoder().encode(s).buffer;
  return hashBuffer(buf);
}
